/**
 * What a file source is (one series per port, S1b and S5; docs/plans/2026-10-25_one-series-per-port.md): its role, in a
 * person's words, as Data sources shows and sets it and as a reviewer sets it for each stream at a reader's approval.
 *
 * A port fed by two sources that cover the same time reads the one that ranks first. A file source ranks by its role:
 * the machine's own recorded log (history, rank 2) before a sheet made for people (report, rank 3); a source with no
 * role reads as a report. Live from the machine ranks 1 and manual entry 4. A person may give any source another rank.
 *
 * At approval each stream of a reader that lands on equipment shows the role its reader says (or none) and lets the
 * reviewer choose one. Left as the reader says, nothing is sent: the server writes the reader's role only where the
 * data source has none, so a role someone set on Data sources is kept. A choice is sent as the stream's `source_role`
 * and written whatever the source holds. Pure.
 */
import { errorCodeOf, errorDetailOf } from "@/lib/api/error";
import type { SourceRole } from "@/lib/api/twin-import";
import { landsOnEquipment, type ManifestStream, type SourceRoleBody } from "./hub";

/** What a role means, inside a sentence. */
export const ROLE_MEANING: Readonly<Record<SourceRole, string>> = {
  history: "the machine's own recorded log",
  report: "a sheet made for people",
};

/** A role in full, as the selects offer it. */
export const ROLE_WORDS: Readonly<Record<SourceRole, string>> = {
  history: `History: ${ROLE_MEANING.history}`,
  report: `Report: ${ROLE_MEANING.report}`,
};

/** No role, as the selects offer it. */
export const NO_ROLE_WORDS = "Not set, read as a report";

/** A role's short name: "History", "Report" or "Not set". */
export function roleName(role: string | null | undefined): string {
  if (role === "history") return "History";
  if (role === "report") return "Report";
  return "Not set";
}

/** A role as stored, or null for none and for anything else. */
export function roleOf(role: unknown): SourceRole | null {
  return role === "history" || role === "report" ? role : null;
}

/** What a port's source is (TwinPortSource.role, PortFeed.source_kind). */
export type SourceKind = "live" | "history" | "report" | "manual";

/** The rank a source reads at by default, by what it is: 1 is read first. */
export const DEFAULT_RANK: Readonly<Record<SourceKind, number>> = { live: 1, history: 2, report: 3, manual: 4 };

/** The rank a file source with this role reads at by default (none reads as a report). */
export function defaultRankOfRole(role: string | null | undefined): number {
  return role === "history" ? DEFAULT_RANK.history : DEFAULT_RANK.report;
}

/** The ranks a person may set. */
export const RANKS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/** A rank select's options: the default (its value "") and each rank a person may set. */
export function rankOptions(defaultRank: number): { value: string; label: string }[] {
  return [
    { value: "", label: `Rank ${defaultRank}, the default` },
    ...RANKS.map((r) => ({ value: String(r), label: `Rank ${r}` })),
  ];
}

/** A rank as a person reads it beside a source: "Rank 2, the default", "Rank 1, set by a person". */
export function rankText(rank: number | null | undefined, set: number | null | undefined, defaultRank: number): string {
  if (typeof set === "number") return `Rank ${set}, set by a person`;
  return `Rank ${typeof rank === "number" ? rank : defaultRank}, the default`;
}

/** The reviewer's choice of a stream's role: as its reader says (nothing is sent), or one of the three. */
export type RoleChoice = "reader" | "history" | "report" | "none";

/** The role a stream's reader says, or null for none. */
export function manifestRoleOf(s: ManifestStream): SourceRole | null {
  return roleOf(s.source_role);
}

/** Whether the review sheet asks a stream's role: only a stream whose rows land on equipment has one. */
export function asksRole(s: ManifestStream): boolean {
  return landsOnEquipment(s);
}

/** A choice as the approval sends it; undefined for as the reader says, which sends nothing. */
export function roleChoiceBody(choice: RoleChoice | undefined): SourceRoleBody | undefined {
  switch (choice) {
    case "history":
    case "report":
      return choice;
    case "none":
      return null;
    default:
      return undefined;
  }
}

/** The approval's roles: one for each stream the reviewer chose one for, and none for the rest. */
export function rolesBody(choices: Readonly<Record<string, RoleChoice>>): Record<string, SourceRoleBody> {
  const out: Record<string, SourceRoleBody> = {};
  for (const [key, choice] of Object.entries(choices)) {
    const body = roleChoiceBody(choice);
    if (body !== undefined) out[key] = body;
  }
  return out;
}

/** A stream's role select: as its reader says (naming what it says), then the three roles. */
export function roleOptions(s: ManifestStream): { value: RoleChoice; label: string }[] {
  const said = manifestRoleOf(s);
  return [
    { value: "reader", label: `As the reader says (${said ? roleName(said).toLowerCase() : "not set"})` },
    { value: "history", label: ROLE_WORDS.history },
    { value: "report", label: ROLE_WORDS.report },
    { value: "none", label: NO_ROLE_WORDS },
  ];
}

/**
 * The role each stream's data source holds now, for a stream that already writes through one, by the key the review
 * sheet and the approval read a stream by: the manifest's own key. The import's summary keys its streams trimmed, so a
 * manifest key with spaces around it is found through its trimmed one, and both keys are given.
 */
export function currentRolesOf(
  summaries: readonly { key: string; data_source_id: string | null }[],
  manifest: readonly { key: string }[],
  dataSources: readonly { id: string; source_role?: unknown }[],
): Record<string, SourceRole | null> {
  const byId = new Map(dataSources.map((d) => [d.id, roleOf(d.source_role)]));
  const out: Record<string, SourceRole | null> = {};
  for (const st of summaries) {
    if (!st.data_source_id || !byId.has(st.data_source_id)) continue;
    const role = byId.get(st.data_source_id) ?? null;
    out[st.key] = role;
    for (const m of manifest) if (m.key.trim() === st.key.trim()) out[m.key] = role;
  }
  return out;
}

/** The line under a stream's role select: what its reader says, and what leaving it does. */
export function roleHint(s: ManifestStream, current?: SourceRole | null): string {
  const said = manifestRoleOf(s);
  const reader = said ? `The reader says this file is ${ROLE_MEANING[said]}.` : "The reader names no role for this file.";
  if (current !== undefined) {
    const now = current ? `Its data source is marked ${roleName(current).toLowerCase()} now.` : "Its data source has no role now.";
    return `${reader} ${now} Left as the reader says, a role already set on Data sources is kept.`;
  }
  return `${reader} Left as the reader says, a role already set on Data sources is kept.`;
}

/** The server's role word in a conflict ("history", "report", "no role") as a person reads it. */
function conflictRole(word: string): string {
  const w = word.trim();
  return w === "no role" ? "not set" : w;
}

/**
 * What a refused approval says, for the refusals about roles: two streams that write into one data source but say
 * different roles (422 source_role_conflict, naming both when the server's sentence does), and a lock the database gave
 * up on (409 try_again: nothing was approved, and sending it again is safe). null for any other refusal.
 */
export function approveRefusalText(err: unknown): string | null {
  const code = errorCodeOf(err);
  if (code === "source_role_conflict") {
    const m = /streams (\S+) and (\S+) write through one data source but say different roles \(([^)]+?) and ([^)]+)\)/.exec(
      errorDetailOf(err) ?? "",
    );
    const fix = "A data source has one role, so set both streams to the same one and approve again.";
    if (m) {
      return `Streams ${m[1]} and ${m[2]} write into one data source, but one is set to ${conflictRole(m[3])} and the other to ${conflictRole(m[4])}. ${fix}`;
    }
    return `Two streams of this reader write into one data source but are set to different roles. ${fix}`;
  }
  if (code === "try_again") {
    return "Another change to the same sources was running at that moment. Nothing was approved. Try again.";
  }
  return null;
}
